import ArrowRightIcon from '@assets/icons/arrow-right-medium.svg';
import i18n from '@/utils/i18n';
import React, { useMemo, useState } from 'react';
import { Button } from '@/components/button';
import { createStyles } from '@/contexts/theme-context';
import { FONTS, TYPOGRAPHY } from '@/styles';
import { getTranslationValue, Translation } from '@/utils/translations';
import { openPDF, openWebBrowserURL } from '@/utils/navigation';
import { RedirectActionSheet } from '@/components/redirect-action-sheet';
import { useToasts } from '@/contexts/toast-context';

export interface AdditionalInformationButtonFields {
  display_text: string;
  display_text_translations?: Translation;
  url: string;
}

export interface ItemInformationConfig {
  additional_information?: {
    additional_information_button?: AdditionalInformationButtonFields;
  };
}

interface Props {
  itemInformation: ItemInformationConfig | null;
  safetyDataSheetURL: string | null;
  sellSheetURL: string | null;
  variantPackUUID: string;
}

export const ItemDetailMetadataLink: React.FC<Props> = ({
  itemInformation,
  safetyDataSheetURL,
  sellSheetURL,
  variantPackUUID,
}) => {
  const { showErrorToast } = useToasts();
  const [styles, theme] = useStyles();

  const [redirectSheetOpen, setRedirectSheetOpen] = useState(false);

  const { buttonText, redirectURL, confirmRedirect } = useMemo(() => {
    // use sellSheetURL if it exists
    if (sellSheetURL) {
      return {
        buttonText: i18n.t('view_spec_sheet'),
        confirmRedirect: false,
        redirectURL: sellSheetURL,
      };
    }

    // if we're not rendering or waiting for sell sheet, fall back to additional info config
    const additionalInfoButtonFields =
      itemInformation?.additional_information?.additional_information_button;
    if (!additionalInfoButtonFields || !additionalInfoButtonFields.url.trim()) {
      return {
        buttonText: null,
        confirmRedirect: false,
        redirectURL: null,
      };
    }

    return {
      buttonText: additionalInfoButtonFields.display_text_translations
        ? getTranslationValue(
            additionalInfoButtonFields.display_text_translations,
          )
        : additionalInfoButtonFields.display_text,
      confirmRedirect: true,
      redirectURL: additionalInfoButtonFields.url.trim(),
    };
  }, [sellSheetURL, itemInformation]);

  const openURL = async (url: string) => {
    try {
      if (url.endsWith('.pdf')) {
        await openPDF(url);
      } else {
        await openWebBrowserURL(url);
      }
    } catch (e) {
      showErrorToast();
    }
  };

  const showPrimaryButton = Boolean(buttonText && redirectURL);

  if (!showPrimaryButton && !safetyDataSheetURL) {
    return null;
  }

  const trackingData = {
    name: 'item_detail.additional_information_button',
    properties: {
      variantPackUUID,
    },
  };

  return (
    <>
      {showPrimaryButton ? (
        <Button
          onPress={() => {
            if (confirmRedirect) {
              setRedirectSheetOpen(true);
            } else if (redirectURL) {
              openURL(redirectURL);
            }
          }}
          rightElement={<ArrowRightIcon fill={theme.colors.primary} />}
          rightElementStyle={styles.rightElement}
          size="small"
          style={styles.buttonStyle}
          textStyle={[TYPOGRAPHY.small, FONTS.primarySemiBold]}
          trackingData={trackingData}
          type="tertiary"
        >
          {buttonText}
        </Button>
      ) : null}
      {safetyDataSheetURL ? (
        <Button
          onPress={() => openURL(safetyDataSheetURL)}
          rightElement={<ArrowRightIcon fill={theme.colors.primary} />}
          rightElementStyle={styles.rightElement}
          size="small"
          style={[
            styles.buttonStyle,
            showPrimaryButton ? styles.stackedButton : null,
          ]}
          textStyle={[TYPOGRAPHY.small, FONTS.primarySemiBold]}
          trackingData={trackingData}
          type="tertiary"
        >
          {i18n.t('view_safety_data_sheet')}
        </Button>
      ) : null}
      {confirmRedirect && redirectSheetOpen ? (
        <RedirectActionSheet
          isRedirectSheetOpen={redirectSheetOpen}
          redirectURL={redirectURL}
          setIsRedirectSheetOpen={setRedirectSheetOpen}
        />
      ) : null}
    </>
  );
};

const useStyles = createStyles((theme) => ({
  buttonStyle: {
    backgroundColor: theme.colors.blueAction,
  },
  rightElement: {
    marginLeft: 8,
  },
  stackedButton: {
    marginTop: 8,
  },
}));
